import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { morphSection, normalizeSectionId } from '@theme/section-renderer';

/**
 * The Section Rendering API answers a request for more than five sections with a 400 and renders
 * nothing, so anything larger falls back to fetching the document and morphing <main> — the same
 * path variant-picker.js already uses for combined listings.
 */
const MAX_SECTIONS_PER_REQUEST = 5;

/** How many products' rendered sections to keep in memory. A style group is rarely larger. */
const MAX_CACHED_PRODUCTS = 12;

/**
 * Gets the main content element that wraps the template's sections.
 * @returns {HTMLElement | null}
 */
function getMain() {
  return document.getElementById('MainContent') ?? document.querySelector('main');
}

/**
 * Collects the section IDs rendered by the current template.
 *
 * The IDs are read from the DOM rather than hardcoded so that a merchant adding or removing a
 * section in the theme editor is picked up without a code change.
 *
 * @returns {string[]} The section IDs.
 */
function getRefreshableSectionIds() {
  const main = getMain();
  if (!main) return [];

  return Array.from(main.querySelectorAll(':scope > .shopify-section'))
    .map((section) => normalizeSectionId(section.id))
    .filter((id) => Boolean(id));
}

/**
 * Toggles the loading state on every style group picker on the page.
 * @param {boolean} loading - Whether a sibling product is being fetched.
 * @param {string} [pathname] - The pathname being fetched, used to mark the clicked swatch.
 */
function setLoading(loading, pathname) {
  for (const picker of document.querySelectorAll('style-group-picker')) {
    picker.toggleAttribute('data-loading', loading);
    picker.setAttribute('aria-busy', String(loading));

    for (const link of picker.querySelectorAll('[data-sibling-product]')) {
      const pending = loading && link instanceof HTMLAnchorElement && new URL(link.href).pathname === pathname;

      link.toggleAttribute('data-pending', pending);
    }
  }
}

/**
 * Writes a message into the picker's live region.
 * @param {string | null | undefined} message - The already translated message.
 */
function announce(message) {
  if (!message) return;

  const status = document.querySelector('style-group-picker [ref="status"]');
  if (status) status.textContent = message;
}

/**
 * Applies the destination product's page title, which Liquid rendered onto the refreshed picker.
 *
 * The canonical link is deliberately left alone: every sibling is a real product with its own
 * canonical URL, and rewriting it client-side would not be seen by crawlers anyway.
 */
function applyPageTitle() {
  const title = document.querySelector('style-group-picker')?.getAttribute('data-page-title');

  if (title) document.title = title;
}

/**
 * Reads the title of the product that is now displayed.
 *
 * A bare product name needs no translation, which keeps the feature from adding theme
 * translation keys that would have to be mirrored across every locale file.
 *
 * @returns {string | null} The product title.
 */
function getSelectedTitle() {
  const current = document.querySelector('style-group-picker [data-sibling-product][aria-current="page"]');

  return current?.getAttribute('data-product-title') ?? null;
}

/**
 * Swaps the product-dependent sections of the page for another product in the same style group.
 *
 * A single instance owns the abort controller, the response cache and the popstate listener, so
 * that replacing the picker markup can never orphan a listener or register a duplicate one.
 */
class StyleGroupNavigator {
  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * Rendered sections keyed by request URL, so returning to an already visited sibling is free.
   * Capped so a long browsing session cannot grow it without bound.
   * @type {Map<string, Record<string, string>>}
   */
  #cache = new Map();

  /** Increments on every navigation so a slow response can never overwrite a newer one. */
  #token = 0;

  /** The pathname currently rendered into the DOM. */
  #currentPath = window.location.pathname;

  /** The pathname currently being fetched, used to ignore repeat clicks on the same swatch. */
  #pendingPath = null;

  #started = false;

  /**
   * Starts listening for history navigations. Safe to call from every picker's connectedCallback.
   */
  start() {
    if (this.#started) return;

    this.#started = true;
    window.addEventListener('popstate', this.#handlePopState);
  }

  #handlePopState = () => {
    // variant-picker.js rewrites the query string with replaceState on every variant change, so
    // only a change of pathname means a different product needs to be rendered.
    if (window.location.pathname === this.#currentPath) return;

    this.navigate(window.location.href, { updateHistory: false });
  };

  /**
   * Renders another product in place.
   * @param {string} target - The destination product URL.
   * @param {object} [options] - The options.
   * @param {boolean} [options.updateHistory] - Whether to push a history entry.
   */
  async navigate(target, { updateHistory = true } = {}) {
    const url = new URL(target, window.location.href);

    // The loading state disables pointer events, but guard in JS too so a keyboard repeat or a
    // page whose CSS has not loaded cannot fire the same request twice.
    if (url.pathname === this.#pendingPath) return;

    const token = ++this.#token;
    this.#pendingPath = url.pathname;

    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    setLoading(true, url.pathname);

    try {
      const sectionIds = getRefreshableSectionIds();
      const rendered =
        sectionIds.length > 0 && sectionIds.length <= MAX_SECTIONS_PER_REQUEST
          ? await this.#renderSections(url, sectionIds, abortController.signal)
          : await this.#renderDocument(url, abortController.signal);

      // A newer swatch was clicked while this request was in flight. Drop this result so the
      // slower response can never replace the product the shopper actually chose last.
      if (token !== this.#token) return;

      if (!rendered) throw new Error(`Could not render ${url.pathname} in place`);

      this.#currentPath = url.pathname;

      if (updateHistory) history.pushState({ styleGroup: true }, '', url.href);

      applyPageTitle();
      announce(getSelectedTitle());
    } catch (error) {
      if (abortController.signal.aborted || token !== this.#token) return;

      console.error(error);

      // Never leave the shopper on a half-updated product page.
      window.location.href = url.href;
    } finally {
      if (token === this.#token) {
        this.#pendingPath = null;
        setLoading(false);
      }
    }
  }

  /**
   * Renders the destination product through the Section Rendering API.
   * @param {URL} url - The destination product URL.
   * @param {string[]} sectionIds - The section IDs to re-render.
   * @param {AbortSignal} signal - The abort signal.
   * @returns {Promise<boolean>} Whether the sections were rendered.
   */
  async #renderSections(url, sectionIds, signal) {
    const requestUrl = new URL(url);
    requestUrl.searchParams.set('sections', sectionIds.join(','));

    const cacheKey = requestUrl.href;
    const useCache = !window.Shopify?.designMode;
    let sections = useCache ? this.#cache.get(cacheKey) : undefined;

    if (!sections) {
      const response = await fetch(requestUrl.href, { signal });

      if (!response.ok) throw new Error(`Section rendering failed with ${response.status}`);

      sections = await response.json();

      // A section that does not exist in the destination product's template comes back as null
      // inside an otherwise successful response, so check before touching the DOM.
      for (const sectionId of sectionIds) {
        if (typeof sections?.[sectionId] !== 'string') return false;
      }

      if (useCache) this.#cacheSections(cacheKey, sections);
    }

    await Promise.all(sectionIds.map((sectionId) => morphSection(sectionId, sections[sectionId])));

    return true;
  }

  /**
   * Stores rendered sections, evicting the oldest entry once the cache is full.
   * @param {string} cacheKey - The request URL.
   * @param {Record<string, string>} sections - The rendered sections.
   */
  #cacheSections(cacheKey, sections) {
    if (this.#cache.size >= MAX_CACHED_PRODUCTS) {
      const oldest = this.#cache.keys().next().value;
      if (oldest) this.#cache.delete(oldest);
    }

    this.#cache.set(cacheKey, sections);
  }

  /**
   * Renders the destination product by morphing the main content, mirroring how variant-picker.js
   * handles combined listings. Used when the template has more sections than one request allows.
   * @param {URL} url - The destination product URL.
   * @param {AbortSignal} signal - The abort signal.
   * @returns {Promise<boolean>} Whether the main content was rendered.
   */
  async #renderDocument(url, signal) {
    const response = await fetch(url.href, { signal });

    if (!response.ok) throw new Error(`Product request failed with ${response.status}`);

    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const main = getMain();
    const newMain = parsed.getElementById('MainContent') ?? parsed.querySelector('main');

    if (!main || !newMain) return false;

    morph(main, newMain, MORPH_OPTIONS);

    return true;
  }
}

const styleGroupNavigator = new StyleGroupNavigator();

/**
 * @typedef {object} StyleGroupPickerRefs
 * @property {HTMLElement} [status] - The live region announcing the displayed product.
 */

/**
 * A custom element that upgrades the style group's product links into an in-place product swap.
 *
 * The links remain real anchors, so without JavaScript — or on a middle/modifier click — the
 * browser navigates to the sibling product normally.
 *
 * @extends {Component<StyleGroupPickerRefs>}
 */
class StyleGroupPicker extends Component {
  connectedCallback() {
    super.connectedCallback();

    styleGroupNavigator.start();
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * Handles a click on a sibling product swatch.
   * @param {MouseEvent} event - The click event.
   */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-sibling-product]') : null;

    if (!(target instanceof HTMLAnchorElement)) return;

    // Leave anything that is not a plain left click to the browser, so opening a colour in a new
    // tab or window keeps working.
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (target.target && target.target !== '_self') return;

    event.preventDefault();

    // Already displayed, so there is nothing to fetch.
    if (target.getAttribute('aria-current') === 'page') return;

    styleGroupNavigator.navigate(target.href);
  };
}

if (!customElements.get('style-group-picker')) {
  customElements.define('style-group-picker', StyleGroupPicker);
}
